import React, { useContext, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Row from 'react-bootstrap/Row'
import Accordion from 'react-bootstrap/Accordion'
import Col from 'react-bootstrap/Col'
import EmployeeDropdown from 'duties/filters/employee-dropdown'
import { useEmployeeComparison } from 'api/hooks/use-employee-comparisons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExclamationTriangle,
  faFolder,
  faAddressCard,
  faExclamationCircle,
  faPowerOff,
  faLayerGroup,
  faRandom
} from '@fortawesome/free-solid-svg-icons'
import { mapAccessType } from 'duties/data-accesses/helper'
import SodExport from 'duties/shared/Export'
import UserContext from 'context/user-context'

const SummaryTable = ({ stats }) => {
  return (
    <table className="table table-border mt-3">
      <tbody>
        {stats.map((stat) => (
          <tr key={stat.name}>
            <td>
              {stat.icon}
              <span className="text-muted ms-1">{stat.value}</span>
              <span className="text-muted ms-1">{stat.name}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function CompareUsers({ setShow, show, employee, employeeId }) {
  const { municipality } = useContext(UserContext)
  const [params, setParams] = useState({})
  const [modalOptions, setModalOptions] = useState({
    showOverlappingPermissions: false
  })
  const handleClose = () => {
    setShow(false)
    setParams({})
  }

  const { data, isSuccess } = useEmployeeComparison({
    params: {
      employee_id: employeeId,
      comparison_id: params.employee_id
    },
    options: {
      enabled: !!params.employee_id
    }
  })

  // Memoized so SodExport's params-sync effect doesn't re-fire every render.
  // include_overlapping mirrors the toggle, so the CSV matches what's on screen.
  const exportParams = useMemo(
    () => ({
      employee_id: employeeId,
      comparison_id: params.employee_id,
      include_overlapping: modalOptions.showOverlappingPermissions
    }),
    [employeeId, params.employee_id, modalOptions.showOverlappingPermissions]
  )

  function groupAndSortPermissionsByCategory(
    permissionsUnion,
    permissionsDiff
  ) {
    const groupPermissions = (permissions, type) => {
      return permissions.reduce((acc, permission) => {
        const key = permission.category_caption || 'Uncategorized'
        if (!acc[key]) acc[key] = { Union: [], Diff: [] }
        acc[key][type].push(permission)
        return acc
      }, {})
    }

    const groupedUnion = groupPermissions(permissionsUnion, 'Union')
    const groupedDiff = groupPermissions(permissionsDiff, 'Diff')

    // Merge Union and Diff groups
    const mergedGroups = { ...groupedUnion }
    Object.keys(groupedDiff).forEach((key) => {
      if (!mergedGroups[key]) mergedGroups[key] = { Union: [], Diff: [] }
      mergedGroups[key].Diff.push(...groupedDiff[key].Diff)
    })

    // Sort each group by permission description
    Object.keys(mergedGroups).forEach((key) => {
      Object.keys(mergedGroups[key]).forEach((type) => {
        mergedGroups[key][type].sort((a, b) =>
          a.description.localeCompare(b.description)
        )
      })
    })

    // Sort groups alphabetically by category
    return Object.keys(mergedGroups)
      .sort((a, b) => a.localeCompare(b))
      .reduce((sortedAcc, key) => {
        sortedAcc[key] = mergedGroups[key]
        return sortedAcc
      }, {})
  }

  function groupDataAccessByTopModuleAndName(dataAccess) {
    return dataAccess.reduce((acc, da) => {
      const topModuleKey = da.data_access.top_module_name || 'Uncategorized'
      const nameKey = da.data_access.name || 'Unnamed'

      if (!acc[topModuleKey]) acc[topModuleKey] = {}
      if (!acc[topModuleKey][nameKey]) acc[topModuleKey][nameKey] = []

      const existingEntry = acc[topModuleKey][nameKey].find(
        (entry) =>
          entry.access_type === da.access_type &&
          entry.start1 === da.start1 &&
          entry.end1 === da.end1
      )

      if (existingEntry) {
        existingEntry.program.push(da.program_name || da.program_id?.toString())
      } else {
        acc[topModuleKey][nameKey].push({
          program: [da.program_name || da.program_id?.toString()],
          access_type: da.access_type,
          start1: da.start1,
          end1: da.end1
        })
      }

      return acc
    }, {})
  }

  const stats = (user) => {
    return [
      {
        name: 'Total Conflicts',
        icon: (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-danger"
          />
        ),
        value: user.conflicts_count
      },
      {
        name: 'Roles',
        icon: <FontAwesomeIcon icon={faAddressCard} className="text-primary" />,
        value: user.roles_count
      },
      {
        name: 'High Risk Permissions',
        icon: (
          <FontAwesomeIcon
            icon={faExclamationCircle}
            className="text-warning"
          />
        ),
        value: user.high_risk_permissions_count
      },
      {
        name: 'Super User Permissions',
        icon: <FontAwesomeIcon icon={faPowerOff} className="text-danger" />,
        value: user.superuser_permissions_count
      },
      {
        name: 'Business Rules',
        icon: <FontAwesomeIcon icon={faLayerGroup} className="text-primary" />,
        value: user.business_rules_count
      },
      {
        name: 'Functional Categories',
        icon: <FontAwesomeIcon icon={faRandom} className="text-primary" />,
        value: user.functional_categories_count
      }
    ]
  }

  const PermissionsAccordion = ({
    union,
    diff,
    groupFunction,
    title,
    showOverlapping
  }) => {
    return (
      <>
        {isSuccess && Object.keys(groupFunction(union, diff)).length > 0 && (
          <div className="mt-2">
            <Accordion alwaysOpen>
              {Object.entries(groupFunction(union, diff)).map(
                ([category, { Union, Diff }], index) => (
                  <Accordion.Item eventKey={index.toString()} key={category}>
                    <Accordion.Header>
                      <FontAwesomeIcon icon={faFolder} className="me-2" />
                      {category}{' '}
                      <small className="text-muted ms-4">
                        Differences: {Diff.length}
                      </small>
                    </Accordion.Header>
                    <Accordion.Body>
                      {showOverlapping && (
                        <div>
                          <strong>Overlapping Permissions:</strong>
                          <ul className="ps-3 list-disc text-success">
                            {Union.map((item) => (
                              <li key={`union-${item.id}`}>
                                {item.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="mt-3">
                        <strong>Differences:</strong>
                        <ul className="ps-3 list-disc">
                          {Diff.map((item) => (
                            <li key={`diff-${item.id}`}>{item.description}</li>
                          ))}
                          {Diff.length === 0 && (
                            <li className="text-muted">
                              This user does not have additional permissions for
                              this category.
                            </li>
                          )}
                        </ul>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                )
              )}
            </Accordion>
          </div>
        )}
        {isSuccess && Object.keys(groupFunction(union, diff)).length === 0 && (
          <div className="mt-2">
            <span>
              No additional {title.toLowerCase()} found for this user.
            </span>
          </div>
        )}
      </>
    )
  }

  const DataAccessAccordion = ({ data, groupFunction, title }) => {
    return (
      <>
        {isSuccess && Object.keys(groupFunction(data)).length > 0 && (
          <div className="mt-2">
            <Accordion alwaysOpen>
              {Object.entries(groupFunction(data)).map(
                ([topModule, names], index) => (
                  <Accordion.Item eventKey={index.toString()} key={topModule}>
                    <Accordion.Header>
                      <FontAwesomeIcon icon={faFolder} className="me-2" />
                      {topModule}
                    </Accordion.Header>
                    <Accordion.Body>
                      {Object.entries(names).map(([name, items]) => (
                        <div key={name} className="mb-3">
                          <strong>{name}</strong>
                          <ul className="ps-3 list-disc">
                            {items.map((item, idx) => (
                              <li key={`${name}-${idx}`}>
                                Access Type:{' '}
                                {mapAccessType(municipality, item.access_type)}{' '}
                                Start: {item.start1}, End: {item.end1}
                                <div className="text-muted small">
                                  Program: {item.program.join(', ')}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </Accordion.Body>
                  </Accordion.Item>
                )
              )}
            </Accordion>
          </div>
        )}
        {isSuccess && Object.keys(groupFunction(data)).length === 0 && (
          <div className="mt-2">
            <span>
              No additional {title.toLowerCase()} found for this user.
            </span>
          </div>
        )}
      </>
    )
  }

  const RolesList = ({ id, roles, diff }) => {
    return (
      <ul className={`my-3 ${diff ? 'triangle-bullets' : ''}`}>
        {roles.map((role) => (
          <li key={`list-${id}-${role.id}`} className="my-1">
            <a href={`/roles/${role.id}`} target="_blank" rel="noreferrer">
              {role.name}
            </a>
            {role.caption && (
              <span className="text-muted small ms-2">{role.caption}</span>
            )}
          </li>
        ))}
      </ul>
    )
  }

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-3">
          <Modal.Title>User Comparison</Modal.Title>
          {isSuccess && (
            <>
              {/* Keyed on the comparison so switching users remounts it and the
                  default export name follows the pair being compared. */}
              <SodExport
                key={`user-comparison-export-${params.employee_id}`}
                params={exportParams}
                defaultName={`User Comparison - ${employee.external_id} vs ${data.comparison.external_id}`}
                source="employeeComparison"
                size="sm"
              />
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="overlapping-permissions"
                  checked={modalOptions.showOverlappingPermissions}
                  onChange={() =>
                    setModalOptions({
                      ...modalOptions,
                      showOverlappingPermissions:
                        !modalOptions.showOverlappingPermissions
                    })
                  }
                />
                <label
                  className="form-check-label small text-muted"
                  htmlFor="overlapping-permissions"
                >
                  Include overlapping permissions
                </label>
              </div>
            </>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        <Row className="sticky-top bg-white py-2 border-bottom">
          <Col sm={6} className="border-end">
            <div className="mt-2 fw-bold">{employee.external_id}</div>
            {!params.employee_id && (
              <div className="text-muted">
                <span>
                  Select a user to compare with {employee.external_id}
                </span>
              </div>
            )}
          </Col>
          <Col sm={6}>
            <EmployeeDropdown setFilterParams={setParams} filterParams={params} />
          </Col>
        </Row>

        {!!params.employee_id && (
          <>
            <Row>
              <Col sm={6} className="border-end">
                {isSuccess && <SummaryTable stats={stats(data.employee)} />}
              </Col>
              <Col sm={6}>
                {isSuccess && <SummaryTable stats={stats(data.comparison)} />}
              </Col>
            </Row>

            <div
              className="p-2 mt-3 rounded-1"
              style={{ backgroundColor: '#f9f9f9' }}
            >
              Roles
            </div>

            <Row className="mt-2 border-top p-2">
              <Col sm={12}>
                <div className="fw-bold text-center underline mb-2">
                  Roles assigned to both users
                </div>
                {isSuccess && data.employee.roles_union.length === 0 && (
                  <div className="text-muted text-center">No roles found</div>
                )}
              </Col>
              <Col sm={6} className="border-end">
                {isSuccess && data.employee.roles_union.length > 0 && (
                  <RolesList
                    roles={data.employee.roles_union}
                    key={`employee-union-${data.employee.id}`}
                    id={`employee-union-${data.employee.id}`}
                    diff={false}
                  />
                )}
              </Col>
              <Col sm={6}>
                {isSuccess && data.comparison.roles_union.length > 0 && (
                  <RolesList
                    roles={data.comparison.roles_union}
                    key={`comparison-union-${data.comparison.id}`}
                    id={`comparison-union-${data.comparison.id}`}
                    diff={false}
                  />
                )}
              </Col>
            </Row>
            <Row className="mt-2 border-top p-2">
              <Col sm={12}>
                <div className="fw-bold text-center underline mb-2">
                  Roles assigned to only one user
                </div>
                {isSuccess &&
                  data.employee.roles_diff.length === 0 &&
                  data.comparison.roles_diff.length === 0 && (
                    <div className="text-muted text-center">No roles found</div>
                  )}
              </Col>

              {isSuccess &&
                (data.employee.roles_diff.length > 0 ||
                  data.comparison.roles_diff.length > 0) && (
                  <>
                    <Col sm={6} className="border-end">
                      <RolesList
                        roles={data.employee.roles_diff}
                        key={`employee-diff-${data.employee.id}`}
                        id={`employee-diff-${data.employee.id}`}
                        diff={true}
                      />
                    </Col>
                    <Col sm={6}>
                      <RolesList
                        roles={data.comparison.roles_diff}
                        key={`comparison-diff-${data.comparison.id}`}
                        id={`comparison-diff-${data.comparison.id}`}
                        diff={true}
                      />
                    </Col>
                  </>
                )}
            </Row>

            <div
              className="p-2 mt-3 rounded-1"
              style={{ backgroundColor: '#f9f9f9' }}
            >
              Permissions
            </div>
            {isSuccess && (
              <Row className="mt-2 border-top p-2">
                <Col sm={6} className="border-end">
                  <PermissionsAccordion
                    diff={data.employee.permissions_diff}
                    union={data.employee.permissions_union}
                    groupFunction={groupAndSortPermissionsByCategory}
                    title="Permissions"
                    showOverlapping={modalOptions.showOverlappingPermissions}
                  />
                </Col>
                <Col sm={6}>
                  <PermissionsAccordion
                    diff={data.comparison.permissions_diff}
                    union={data.comparison.permissions_union}
                    groupFunction={groupAndSortPermissionsByCategory}
                    title="Permissions"
                    showOverlapping={modalOptions.showOverlappingPermissions}
                  />
                </Col>
              </Row>
            )}
            <div
              className="p-2 mt-3 rounded-1"
              style={{ backgroundColor: '#f9f9f9' }}
            >
              Data Access
            </div>
            {isSuccess && (
              <Row className="mt-2 border-top p-2">
                <Col sm={6} className="border-end">
                  <DataAccessAccordion
                    data={data.employee.data_access}
                    groupFunction={groupDataAccessByTopModuleAndName}
                    title="Data Access"
                  />
                </Col>
                <Col sm={6}>
                  <DataAccessAccordion
                    data={data.comparison.data_access}
                    groupFunction={groupDataAccessByTopModuleAndName}
                    title="Data Access"
                  />
                </Col>
              </Row>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default CompareUsers
